import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState, type ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ImagePlus, Lock, X } from 'lucide-react';
import { toast } from 'sonner';
import { HOMEWORK_PHOTOS_MAX, HOMEWORK_PHOTO_MAX_BYTES } from '@repo/shared-types';
import { cn } from '@repo/ui/src/lib/utils';
import { PhotoLightbox, type LightboxPhoto } from '@repo/photo-lightbox';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { compressToJpeg, rejectReason } from '../../lib/imageCompress';
import { presignHomeworkPhoto, uploadWithRepresign } from '../../lib/upload';
import { useUpgradeDialogStore } from '../../stores/upgradeDialogStore';
import type { DraftPhoto } from './noteDraft';

/**
 * E4 (handoff 21 §2): the photo row of a homework group. Two placements from
 * one component — `card-row` is the last row INSIDE the homework
 * `NoteItemTable` card (template subjects), `below-textarea` is a plain row
 * under the free BTVN textarea. Renders nothing at all while the group has no
 * photos and the drop row is closed, so a teacher who never uses photos sees
 * a form that did not grow by a pixel.
 *
 * Upload happens on pick, never on save: reject → compress → presign (with
 * the count of photos this edit already added) → PUT with progress, one
 * re-presign on a 403. Failures toast per file and drop that file; the
 * others continue. The list itself lives in the panel's draft (`photos`).
 */
export interface HomeworkPhotoStripHandle {
  /** Phone: open the OS picker directly. Desktop: open the drop row. */
  openAdd: () => void;
}

const DROP_OPEN_EVENT = 'lophoc:homework-photo-drop-open';
const MAX_PHONE = 4;
const MAX_DESKTOP = 6;

type Props = {
  photos: DraftPhoto[];
  onChange: (next: DraftPhoto[]) => void;
  sessionId: string;
  tier: 'CLASS' | 'STUDENT';
  studentId?: string;
  /** "Bài chung" / "Riêng {tên}" for the lightbox caption. */
  scopeLabel: string;
  /** "{Môn} · {Thứ, ngày} · {giáo viên}" for the lightbox subtitle. */
  subtitle: string;
  variant: 'card-row' | 'below-textarea';
  /** Reported so the label-row button can hide while this row is visible. */
  onVisibilityChange?: (visible: boolean) => void;
};

export const HomeworkPhotoStrip = forwardRef<HomeworkPhotoStripHandle, Props>(function HomeworkPhotoStrip(
  { photos, onChange, sessionId, tier, studentId, scopeLabel, subtitle, variant, onVisibilityChange },
  ref,
) {
  const { t } = useTranslation('progress');
  const isMobile = useIsMobile();
  const [dropOpen, setDropOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Async uploads mutate the list from a ref so two files in flight never
  // clobber each other's entries with a stale closure.
  const latest = useRef(photos);
  useEffect(() => { latest.current = photos; }, [photos]);
  const commit = useCallback((next: DraftPhoto[]) => { latest.current = next; onChange(next); }, [onChange]);

  const visible = photos.length > 0 || dropOpen;
  useEffect(() => { onVisibilityChange?.(visible); }, [visible, onVisibilityChange]);

  // One open drop row per panel: every open row would otherwise attach its
  // own drop listener to the same [role=dialog] and a dropped file would
  // upload once per row (review I3). Opening one closes the others.
  const dropId = useId();
  const openDrop = useCallback(() => {
    window.dispatchEvent(new CustomEvent(DROP_OPEN_EVENT, { detail: dropId }));
    setDropOpen(true);
  }, [dropId]);
  useEffect(() => {
    const onOther = (e: Event) => { if ((e as CustomEvent<string>).detail !== dropId) setDropOpen(false); };
    window.addEventListener(DROP_OPEN_EVENT, onOther);
    return () => window.removeEventListener(DROP_OPEN_EVENT, onOther);
  }, [dropId]);

  useImperativeHandle(ref, () => ({
    openAdd: () => {
      if (isMobile) inputRef.current?.click();
      else openDrop();
    },
  }), [isMobile, openDrop]);

  // Object URLs for previews are revoked when the strip unmounts.
  useEffect(() => () => {
    for (const p of latest.current) if (p.previewUrl.startsWith('blob:')) URL.revokeObjectURL(p.previewUrl);
  }, []);

  const addFiles = useCallback(async (files: File[]) => {
    const live = () => latest.current.filter((p) => p.status !== 'failed');
    const room = HOMEWORK_PHOTOS_MAX - live().length;
    if (files.length > room) toast.error(t('notes.photos.limitToast', { max: HOMEWORK_PHOTOS_MAX }));
    const accepted = files.slice(0, Math.max(0, room));
    for (const file of accepted) {
      const reason = rejectReason(file, HOMEWORK_PHOTO_MAX_BYTES);
      if (reason) {
        toast.error(t('notes.photos.failToast', { name: file.name, reason: t(`notes.photos.reason.${reason}`, { max: HOMEWORK_PHOTO_MAX_BYTES / (1024 * 1024) }) }));
        continue;
      }
      const tempId = `tmp-${Math.random().toString(36).slice(2, 10)}`;
      const previewUrl = URL.createObjectURL(file);
      commit([...latest.current, { id: tempId, key: '', width: 0, height: 0, bytes: file.size, status: 'uploading', progress: 0, previewUrl, saved: false }]);
      const patch = (id: string, fn: (p: DraftPhoto) => DraftPhoto | null) =>
        commit(latest.current.flatMap((p) => {
          if (p.id !== id) return [p];
          const next = fn(p);
          return next ? [next] : [];
        }));
      try {
        const { blob, width, height } = await compressToJpeg(file);
        const fileType = blob.type === 'image/png' ? 'image/png' : 'image/jpeg';
        // Everything in this group's draft except the one being presigned.
        const pendingCount = latest.current.filter((p) => p.status !== 'failed' && p.id !== tempId).length;
        const { key, photoId } = await uploadWithRepresign(
          blob,
          () => presignHomeworkPhoto(sessionId, { tier, studentId, fileType, bytes: blob.size, pendingCount }),
          (pct) => patch(tempId, (p) => ({ ...p, progress: pct })),
        );
        patch(tempId, (p) => ({ ...p, id: photoId, key, width, height, bytes: blob.size, status: 'ready', progress: 100 }));
      } catch (err) {
        const code = (err as { code?: string } | null)?.code;
        patch(tempId, () => null);
        URL.revokeObjectURL(previewUrl);
        if (code === 'PHOTO_LIMIT') toast.error(t('notes.photos.limitToast', { max: HOMEWORK_PHOTOS_MAX }));
        else if (code === 'FEATURE_LOCKED') useUpgradeDialogStore.getState().open('progressLink');
        else toast.error(t('notes.photos.failToast', { name: file.name, reason: t('notes.photos.reason.upload') }));
      }
    }
  }, [commit, sessionId, tier, studentId, t]);

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length) void addFiles(files);
  };

  // Drop target = the nearest [role=dialog] (the Sheet or the vaul Drawer),
  // only while the row is open; this box is just the visual (trap 4).
  useEffect(() => {
    if (!dropOpen || isMobile) return;
    const dialog = rootRef.current?.closest('[role=dialog]') as HTMLElement | null;
    if (!dialog) return;
    const over = (e: DragEvent) => { e.preventDefault(); setDragging(true); };
    const leave = (e: DragEvent) => { if (!dialog.contains(e.relatedTarget as Node | null)) setDragging(false); };
    const drop = (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) void addFiles(files);
    };
    dialog.addEventListener('dragover', over);
    dialog.addEventListener('dragleave', leave);
    dialog.addEventListener('drop', drop);
    return () => {
      dialog.removeEventListener('dragover', over);
      dialog.removeEventListener('dragleave', leave);
      dialog.removeEventListener('drop', drop);
    };
  }, [dropOpen, isMobile, addFiles]);

  const remove = (id: string) => {
    const gone = latest.current.find((p) => p.id === id);
    if (gone?.previewUrl.startsWith('blob:')) URL.revokeObjectURL(gone.previewUrl);
    commit(latest.current.filter((p) => p.id !== id));
    setLightboxIndex(null);
  };

  const input = <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} />;
  if (!visible) return input;

  const live = photos.filter((p) => p.status !== 'failed');
  const max = isMobile ? MAX_PHONE : MAX_DESKTOP;
  const overflow = live.length > max ? live.length - (max - 1) : 0;
  const shown = overflow ? live.slice(0, max - 1) : live;
  const tile = isMobile ? 'h-[52px] w-[52px]' : 'h-[48px] w-[48px]';
  const lightboxPhotos: LightboxPhoto[] = live.filter((p) => p.status === 'ready').map((p) => ({ id: p.id, url: p.previewUrl, width: p.width, height: p.height, captionScope: scopeLabel }));

  const dropRow = dropOpen && !isMobile && (
    <div className="flex items-center gap-2">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        className={cn(
          'flex min-h-[64px] flex-1 cursor-pointer items-center justify-center gap-2.5 rounded-lg border-[1.5px] border-dashed px-3 py-2',
          dragging ? 'border-brand-500 bg-brand-50/50' : 'border-[oklch(0.80_0.02_156)] bg-[oklch(0.997_0.003_155)] hover:border-brand-500 hover:bg-brand-50/50',
        )}
      >
        <ImagePlus className="h-5 w-5 shrink-0 text-surface-500" strokeWidth={1.8} />
        <div className="leading-[1.35]">
          <p className="text-[13px] font-medium text-surface-600">
            <Trans t={t} i18nKey="notes.photos.dropTitle" components={{ 1: <span className="text-brand-700" /> }} />
          </p>
          <p className="mt-0.5 text-[11.5px] text-surface-400">{t('notes.photos.dropHint', { max: HOMEWORK_PHOTOS_MAX })}</p>
        </div>
      </div>
      <button type="button" aria-label={t('notes.photos.dismiss')} onClick={() => setDropOpen(false)} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-surface-400 hover:text-surface-600">
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );

  const thumbs = live.length > 0 && (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((p) => (
        <div key={p.id} className={cn('relative shrink-0 overflow-hidden rounded-md bg-surface-100', tile)}>
          <button type="button" aria-label={t('lightbox.homework')} onClick={() => p.status === 'ready' && setLightboxIndex(lightboxPhotos.findIndex((l) => l.id === p.id))} className="block h-full w-full">
            <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
          </button>
          {p.status === 'uploading' && (
            <>
              <div className="pointer-events-none absolute inset-0 bg-white/55" />
              <div className="pointer-events-none absolute bottom-1.5 left-1.5 right-1.5 h-[3px] overflow-hidden rounded-full bg-surface-100">
                <div className="h-full rounded-full bg-brand-500" style={{ width: `${p.progress}%` }} />
              </div>
            </>
          )}
          {p.status === 'ready' && (
            <button type="button" aria-label={t('notes.photos.remove')} onClick={() => remove(p.id)} className="absolute right-0.5 top-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-black/55 text-white">
              <X className="h-2.5 w-2.5" strokeWidth={2.5} />
            </button>
          )}
        </div>
      ))}
      {overflow > 0 && (
        <button type="button" onClick={() => setLightboxIndex(Math.min(shown.length, Math.max(0, lightboxPhotos.length - 1)))} className={cn('shrink-0 rounded-md bg-surface-900 text-[13px] font-semibold text-white', tile)}>
          {t('notes.photos.more', { n: overflow })}
        </button>
      )}
      <button
        type="button"
        aria-label={t('notes.photos.add')}
        onClick={() => (isMobile ? inputRef.current?.click() : openDrop())}
        className={cn('flex shrink-0 items-center justify-center rounded-md border-[1.5px] border-dashed text-surface-500', dragging ? 'border-brand-500 bg-brand-50/50' : 'border-[oklch(0.83_0.011_156)]', tile)}
      >
        <ImagePlus className="h-[18px] w-[18px]" strokeWidth={1.8} />
      </button>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={cn(
        variant === 'card-row'
          ? 'flex flex-col gap-2 border-t border-surface-100 bg-surface-50 px-3 py-2.5'
          : 'mt-[6.4px] flex flex-col gap-2',
      )}
    >
      {input}
      {thumbs}
      {dropRow}
      <PhotoLightbox
        photos={lightboxPhotos}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        subtitle={() => subtitle}
        labels={{
          homework: t('lightbox.homework'), download: t('lightbox.download'), close: t('lightbox.close'), remove: t('notes.photos.remove'),
          counter: (i, n) => t('lightbox.counter', { i, n }),
        }}
        onDelete={(photo) => remove(photo.id)}
      />
    </div>
  );
});

/**
 * The quiet "Thêm ảnh" button that sits at the right of the homework LABEL
 * (E4 frame 2): 12px/500 surface-500, `image-plus` 13px, 24px tall on
 * desktop / 28px on phone. Three plan states (§4.4): `undefined` while the
 * subscription loads = normal look, no-op; `false` = locked, opens the
 * upgrade dialog; `true` = works. Hidden while the strip itself is showing.
 */
export function HomeworkPhotoAddButton({ onOpen, planAllows, hidden }: { onOpen: () => void; planAllows: boolean | undefined; hidden: boolean }) {
  const { t } = useTranslation('progress');
  if (hidden) return null;
  const locked = planAllows === false;
  return (
    <button
      type="button"
      onClick={() => {
        if (planAllows === undefined) return;
        if (locked) useUpgradeDialogStore.getState().open('progressLink');
        else onOpen();
      }}
      title={locked ? t('notes.photos.locked') : undefined}
      className="ml-auto inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-1.5 text-[12px] font-medium text-surface-500 hover:bg-surface-100 sm:h-6"
    >
      {locked ? <Lock className="h-[13px] w-[13px]" strokeWidth={2} /> : <ImagePlus className="h-[13px] w-[13px]" strokeWidth={1.8} />}
      {t('notes.photos.add')}
    </button>
  );
}
